import { CourseModel } from '@/models/content/course';
import { taxonomySerializer } from './taxonomy';
import { sessionService } from '@/providers/services/auth/session';
import { DEFAULT_IMAGES_PATH, TAXONOMY_LEVELS } from '@/utils/constants';
import { findAnyCompetencyTitle, gutCourseNames, truncateString } from '@/utils/utils';
import moment from 'moment';

export class CourseSerializer {
  private static INSTANCE = new CourseSerializer();

  static get instance() {
    return this.INSTANCE;
  }

  public serializeCourse(course: any): CourseModel {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const thumbnailUrl = course.thumbnail ?
      contentCdnUrl + course.thumbnail :
      `${window.location.origin}` + DEFAULT_IMAGES_PATH.course;
    const result: CourseModel = {
      id: course.id,
      title: course.title,
      description: course.description,
      format: course.format || 'course',
      efficacy: course.efficacy,
      relevance: course.relevance,
      engagement: course.engagement,
      standards: taxonomySerializer.normalizeTaxonomyObject(course.taxonomy),
      creator: taxonomySerializer.normalizeCreator(course.creator),
      owner: taxonomySerializer.normalizeOwner(course.owner),
      unitCount: course.unitCount,
      thumbnailUrl,
    };
    return result;
  }

  /**
   * Normalize the Search course response
   *
   * @param payload is the endpoint response in JSON format
   * @returns {Course[]}
   */
  public serializeCourses(payload: any) {
    const resultSet = {
      searchResults: [],
      hitCount: payload.totalHitCount,
    };
    if (Array.isArray(payload.searchResults)) {
      const results = payload.searchResults.map((result: any) => {
        return this.serializeCoursesList(result);
      });
      resultSet.searchResults = results;
    }
    return resultSet;
  }

  /**
   * Normalizes a course
   * @param {*} result
   * @returns {Course}
   */
  public serializeCoursesList(result: any) {
    const cdnUrls = sessionService.getCdnUrl();
    const contentCdnUrl = cdnUrls.content_cdn_url;
    const basePath = contentCdnUrl;
    const appRootPath = window.location.origin; // configuration appRootPath
    const thumbnailUrl = result.thumbnail
      ? basePath + result.thumbnail
      : appRootPath + DEFAULT_IMAGES_PATH.course;
    const taxonomyInfo =
      (result.taxonomy &&
        result.taxonomy.curriculum &&
        result.taxonomy.curriculum.curriculumInfo) ||
      [];
    const standards = taxonomySerializer
    .normalizeTaxonomyArray(taxonomyInfo, TAXONOMY_LEVELS.COURSE);
    return {
      id: result.id,
      title: result.title,
      audience: result.audience || null,
      description: result.description || findAnyCompetencyTitle(standards),
      createdDate: result.addDate,
      thumbnailUrl,
      lastModified: result.lastModified,
      lastModifiedBy: result.lastModifiedBy,
      subject: result.subjectBucket,
      taxonomySubject: result.taxonomy ? result.taxonomy.subject : null,
      taxonomyCourse: result.taxonomy ? result.taxonomy.course : null,
      taxonomyDomain: result.taxonomy ? result.taxonomy.taxonomyDomain : null,
      subjectSequence: result.subjectSequence,
      isVisibleOnProfile: result.visibleOnProfile,
      isPublished: result.publishStatus === 'published',
      unitCount: result.unitCount,
      assessmentCount: result.assessmentCount,
      collectionCount: result.collectionCount,
      lessonCount: result.lessonCount,
      standards,
      owner: result.owner ? taxonomySerializer.normalizeOwner(result.owner) : null,
      creator: result.creator ? taxonomySerializer.normalizeCreator(result.creator) : null,
      sequence: result.sequence,
      relevance: result.relevance,
      efficacy: result.efficacy,
      engagement: result.engagement,
      type: result.format,
    };
  }

  public coursePulloutSerializer(course: any, coursePullOutData: any) {
    const defaultVectorValue = 0.5;
    let resultSet = {};
    if (course) {
      resultSet = {
        descriptive: {
          title: course.title,
          description: truncateString(course.description),
        },

        creation: {
          'Created On':
            moment(course.createdDate).format('MMMM DD, YYYY') || null,
          'Publisher': 'Gooru Org',
          'Publish Status': course.isPublished ? 'Published' : 'Unpublished',
          'Aggregator': course.aggregator ? course.aggregator : null,
          'Modified On':
            moment(course.lastModified).format('MMMM DD, YYYY') || null,
          'Modified By':
            course.lastModifiedUser.username || course.lastModifiedBy,
          'License': coursePullOutData.license
            ? coursePullOutData.license
            : 'Public Domain',
          'Host': null,
        },

        educational: {
          Audience: course.audience,
        },

        media: {
          Keywords: null,
          Visibility: coursePullOutData.visible_on_profile ? 'True' : 'False',
        },

        instructional: {
          '21st Century Skills': null,
        },

        framework: {
          subject: course.taxonomySubject,
          Course: gutCourseNames(course.taxonomyCourse),
          domain: course.taxonomyDomain,
          Standards: course.standards || [],
        },

        vector: {
          relevance: course.relevance || defaultVectorValue,
          engagement: course.engagement || defaultVectorValue,
          efficacy: course.efficacy || defaultVectorValue,
        },
      };
    }
    return resultSet;
  }

  public normalizeCourseContent(courseData: any) {
   const serializer = this;
   const serializedCourseData = courseData;
   const taxonomy = taxonomySerializer.normalizeTaxonomyObject(courseData.taxonomy);
   serializedCourseData.taxonomy = taxonomy;
   const aggregatedTaxonomy = taxonomySerializer.normalizeTaxonomyArray(courseData.aggregated_taxonomy, false);
   serializedCourseData.aggregated_taxonomy = aggregatedTaxonomy;
   return serializedCourseData;
 }

}

export const courseSerializer = CourseSerializer.instance;
